import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, OnDestroy } from '@angular/core';
import {
    AuthenticationResult,
    PublicClientApplication,
} from '@azure/msal-browser';
import { PopupRequest } from '@azure/msal-browser/dist/request/PopupRequest';
import { Observable, of, Subject } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { AppConfig, CMPS_APP_CONFIG } from '../../app.config';
import { catchErrorLogRet } from '../../common/rest.operators';
import { FeatureStatus } from '../core';

import { parseWwwAuthenticate } from './parse-www-authenticate.function';

/** TODO revise. this could be generic and could move to a core module */
@Injectable()
export class AuthService implements OnDestroy {
    gettingToken: boolean | null;
    tokenChange: Observable<string | null>;

    private readonly _tokenClient: PublicClientApplication;
    private readonly _tokenChangeSubj: Subject<string | null>;

    constructor(
        private readonly _http: HttpClient,
        @Inject(CMPS_APP_CONFIG) private readonly _appConfig: AppConfig,
    ) {
        this._tokenClient = new PublicClientApplication({
            auth: {
                clientId: this._appConfig.MSAL?.CLIENT_ID,
                redirectUri: this._appConfig.MSAL?.REDIRECT_URI,
            },
        });

        this._tokenChangeSubj = new Subject();
        this.tokenChange = this._tokenChangeSubj.asObservable();
    }

    ngOnDestroy(): void {
        this._tokenChangeSubj.complete();
    }

    async getToken(userId: number): Promise<string | null> {
        if (
            this._appConfig.MSAL?.CLIENT_ID == null ||
            this._appConfig.MSAL?.REDIRECT_URI == null ||
            this._appConfig.MSAL?.SCOPES == null
        ) {
            console.error('clientId or redirectUrl or scopes undefined');
            return null;
        }

        if (this.gettingToken === true) {
            console.error('already getting token');
            return null;
        }

        this.gettingToken = true;

        const token = await this._verifyBackendConsent(
            userId,
            (await this._login())?.accessToken,
        );

        this.gettingToken = false;
        this._tokenChangeSubj.next(token);

        return token;
    }

    verifyConfig(): Observable<FeatureStatus[]> {
        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.TEAMS_CALL}/msft/verify-config`,
            )
            .pipe(catchErrorLogRet([]));
    }

    private async _login(): Promise<AuthenticationResult> {
        const acc = this._tokenClient.getAllAccounts()?.[0];
        if (acc != null) {
            try {
                this._tokenClient.setActiveAccount(acc);
                return await this._tokenClient.acquireTokenSilent({
                    scopes: this._appConfig.MSAL?.SCOPES,
                });
            } catch (err) {
                console.log(err);
            }
        }

        try {
            return await this._tokenClient.loginPopup({
                scopes: this._appConfig.MSAL?.SCOPES,
            });
        } catch (err) {
            console.log(err);
        }

        return null;
    }

    private async _verifyBackendConsent(
        userId: number,
        accessToken: string | null,
    ): Promise<string | null> {
        try {
            const resp = await this._http
                .post(
                    `${this._appConfig.API_ENDPOINTS.TEAMS_CALL}/msft/verify-consent`,
                    {
                        userId,
                        accessToken,
                    },
                    { observe: 'response' },
                )
                .pipe(
                    catchError((err) => {
                        return of(err);
                    }),
                )
                .toPromise();

            if (resp.status === 200) {
                return accessToken;
            }

            if (resp.status !== 403) {
                return null;
            }

            const headerStr = resp.headers.get('WWW-Authenticate');
            const opts = parseWwwAuthenticate(headerStr);

            if (opts?.scopes == null || opts.scopes.length === 0) {
                return null;
            }

            const backInst = new PublicClientApplication({
                auth: {
                    clientId: opts.clientId,
                    authority: opts.authority,
                    redirectUri: this._appConfig.MSAL?.REDIRECT_URI,
                },
            });

            const loginReq: PopupRequest = {
                scopes: opts.scopes,
                prompt: opts.proposedAction ?? 'consent',
            };

            return (await backInst.loginPopup(loginReq))?.accessToken;
        } catch (err) {
            // TODO ask user to try again since if loginPopup() was executed for the spa-app-clientId within the same callstack, the second loginPopup() may not run
            console.log(err);
        }

        return null;
    }
}
